import { useMemo, useState } from 'react';

import {
  ArrowDown01Icon,
  CancelCircleIcon,
  CheckmarkCircle02Icon,
  Clock01Icon,
  ComputerIcon,
  Download01Icon,
  File02Icon,
  RestoreBinIcon,
  SentIcon,
  StopCircleIcon,
  ViewIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { Permissions } from 'constants/permissions';
import { RESULT_OPTIONS } from 'constants/result-options';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { getRenewalStatus } from 'helpers/renewals-helper';
import { screeningVerificationBadge } from 'helpers/screeningVerificationBadge';
import { useAccessControl } from 'hooks/access-control';
import { useTracking } from 'hooks/analytic-tracking';
import { useLocale } from 'hooks/locale';
import { Collapse } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';

import { ActionsMenu } from 'components/ActionsMenu/ActionsMenu';
import Alert from 'components/Alert/Alert';
import { Select } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import { KeyValueModal } from 'components/KeyValueModal/KeyValueModal';
import { MetadataDetailsModal } from 'components/MetadataDetailsModal/MetadataDetailsModal';
import { StatusPill } from 'components/Pills/StatusPills';
import { CustomToast } from 'components/Toast/CustomToast';

import styles from './CheckRenewalRow.module.scss';

export const CheckRenewalRow = ({
  candidateCheck,
  candidate,
  checkType,
  companyCheck,
  company,
  renewalConfig,
  renewalCheck,
  previousChecks,
  permissions,
  useViewResult = true,
  useDownloadResult = true,
  user,
  setLoading = () => {},
  getCandidateCheckResult = () => {},
  handleCancelCheck = () => {},
  handleCheckUpdate = () => {},
  handleRenewalUpdate = () => {},
  handleRenewNow = () => {},
}) => {
  const [showMetadata, setShowMetadata] = useState(false);
  const [showExtraDetails, setShowExtraDetails] = useState(false);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [previousChecksDropdownOpen, setPreviousChecksDropdownOpen] = useState(false);
  const { dateSlashFormat } = useLocale(company?.locale);

  const hasAccess = useAccessControl(permissions, { notPermissionPolicy: 'allowed' });

  const { t } = useTranslation();
  const { trackEvent } = useTracking(user);

  const renewalStatus = useMemo(
    () => getRenewalStatus(candidateCheck?.status),
    [candidateCheck?.status],
  );

  const onCancelCheck = checkId => {
    setShowCancelModal(false);
    handleCancelCheck(checkId);

    trackEvent('Check Cancelled', { candidateId: candidate.id, checkType: checkType.title });
  };

  const onChangeStatus = (checkId, statusValue) => {
    if (candidateCheck.user_review_status === statusValue) return;

    handleCheckUpdate(checkId, { user_review_status: statusValue });
    trackEvent('Check Review Status', { candidateId: candidate.id, reviewStatus: statusValue });
  };

  const checkViewResult = (model, model_type) => {
    if (!model?.upload_id) {
      showToast('There is no result to view', 'info');
      return;
    }

    const uri = `${process.env.REACT_APP_WEB_URL}/documents/${model.upload_id}/${model_type}/${model.id}`;
    window.open(uri, '_blank').focus();
  };

  const downloadResult = async () => {
    setLoading(true);
    try {
      await getCandidateCheckResult({
        check: candidateCheck,
        candidate,
        isFile: checkType.result_type === RESULT_OPTIONS.File,
      }).unwrap();

      trackEvent('Check Report Download', {
        candidateId: candidate.id,
        checkType: checkType.title,
      });
    } catch (error) {
      showToast('Oops Error Downloading the File ');
      console.error('errow downloading sub check result file : ', error);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (title, type = 'error') => {
    toast.custom(
      t => (
        <CustomToast
          t={t}
          title={title}
          message={`Please try again or if it continues, please contact the support team.`}
          type={type}
        />
      ),
      {
        duration: 5000,
      },
    );
  };

  const canAccessResults = useMemo(() => {
    return companyCheck.has_sensitive_results
      ? hasAccess(Permissions.CandidateChecksSensitiveResults)
      : hasAccess(Permissions.CandidateChecksResults);
  }, [companyCheck, user]);

  const getExtraDetails = checkExtraData => {
    if (!checkExtraData) return null;

    let filteredExtraData = {};
    Object.keys(checkExtraData).forEach(key => {
      if (checkType.extra_reporting_columns?.includes(key)) {
        filteredExtraData[key] = checkExtraData[key];
      }
    });

    companyCheck.extra_result_fields?.forEach(field => {
      filteredExtraData[field] = checkExtraData[field];
    });

    return filteredExtraData;
  };

  const screeningFrequency = useMemo(() => {
    if (!renewalConfig) return '-';

    if (renewalConfig.renewal_mode === 'use_expiration') {
      return 'Expiration';
    }

    return `${renewalConfig.frequency} days`;
  }, [renewalConfig]);

  return (
    <div className={cn(styles.root, { [styles.rootDummy]: candidateCheck.dummy })}>
      <Alert
        show={showCancelModal}
        title={t('check.backgroundChecks.cancelCheckTitle', { checkType: checkType.title })}
        ok={t('check.backgroundChecks.cancelCheckOk')}
        cancel={t('check.backgroundChecks.cancelCheckCancel')}
        onOk={() => {
          onCancelCheck(candidateCheck.id);
        }}
        onCancel={() => {
          setShowCancelModal(false);
        }}
      />

      <div className={styles.row}>
        <MetadataDetailsModal
          visible={showMetadata}
          onClose={() => setShowMetadata(false)}
          userAgent={candidateCheck.user_agent}
          country={candidateCheck.country}
        />
        {candidateCheck.extra_data && (
          <KeyValueModal
            visible={showExtraDetails}
            onClose={() => setShowExtraDetails(false)}
            title="More Details"
            data={getExtraDetails(candidateCheck.extra_data)}
          />
        )}

        <div className={cn(styles.logoColumn)}>
          <IconComponent icon={getCheckTypeIcon(checkType?.slug)} />
        </div>

        <div className={cn(styles.checkTypeColumn)}>
          <div className={cn('t-small', styles.columnTitle)}>
            {t('components.checkRenewalRow.checkType')}
          </div>
          <div className={cn('u-text-ellipsis', styles.columnValue)}>
            {renewalCheck && (
              <span
                className={cn(styles.renewalStatusCircle, {
                  [styles.renewalStatusCircleCanceled]: renewalCheck.canceled,
                  [styles.renewalStatusCircleApproved]:
                    renewalCheck.approved && !renewalCheck.canceled,
                  [styles.renewalStatusCircleNotApproved]:
                    !renewalCheck.approved && !renewalCheck.canceled,
                })}
              />
            )}
            {checkType.title}
          </div>
        </div>

        <div className={cn(styles.complianceTypeColumn)}>
          <div className={cn('t-small', styles.columnTitle)}>
            {t('components.checkRenewalRow.complianceType')}
          </div>
          <div className={cn('u-text-ellipsis', styles.columnValue)}>
            {renewalConfig ? renewalConfig.name : '-'}
          </div>
        </div>

        <div className={cn(styles.screenedEveryColumn)}>
          <div className={cn('t-small', styles.columnTitle)}>
            {renewalConfig?.renewal_mode === 'use_expiration'
              ? t('components.checkRenewalRow.screenedOn')
              : t('components.checkRenewalRow.screenedEvery')}
          </div>
          <div className={cn('u-text-ellipsis', styles.columnValue)}>{screeningFrequency}</div>
        </div>

        <div className={cn(styles.lastScreenedColumn)}>
          <div className={cn('t-small', styles.columnTitle)}>
            {t('components.checkRenewalRow.lastScreened')}
          </div>
          <div className={cn('u-text-ellipsis', styles.columnValue)}>
            {renewalCheck ? (
              <>{renewalCheck.last_renew_at || '-'}</>
            ) : (
              <Moment format={dateSlashFormat}>{candidateCheck.created_at}</Moment>
            )}
          </div>
        </div>

        <div className={cn(styles.nextReScreenColumn)}>
          <div className={cn('t-small', styles.columnTitle)}>
            {t('components.checkRenewalRow.nextReScreen')}
          </div>
          <div className={cn('u-text-ellipsis', styles.columnValue)}>
            {renewalCheck?.next_renew_at || '-'}
          </div>
        </div>

        <div className={cn(styles.resultFlagColumn)}>
          <span className={cn(styles.columnValue)}>
            {screeningVerificationBadge(candidateCheck, checkType)}
          </span>
        </div>

        <div className={cn(styles.reviewColumn)}>
          {hasAccess(Permissions.CandidateWrite) && candidateCheck.status === 'completed' && (
            <Select
              className={cn(
                'u-margin-right',
                'd-inline-flex',
                styles.reviewSelect,
                styles.columnValue,
              )}
              inputProps={{ onChange: e => onChangeStatus(candidateCheck.id, e.target.value) }}
              value={candidateCheck.user_review_status}
              placeholder="Add Review"
              rounded
              small>
              <Select.Item value="approved">Approved</Select.Item>
              <Select.Item value="not_approved">Not Approved</Select.Item>
              <Select.Item value="needs_review">Needs Review</Select.Item>
            </Select>
          )}
        </div>

        <div className={cn(styles.statusColumn)}>
          <StatusPill
            className={styles.columnValue}
            status={candidateCheck.status}
            text={renewalStatus}
            large
            capitalize
          />
        </div>

        <div
          className={cn(
            styles.actionsColumn,
            'd-flex',
            'justify-content-end',
            'align-items-center',
          )}>
          <ActionsMenu newIconLibrary>
            <ActionsMenu.Item
              newIconLibrary
              icon={File02Icon}
              title="More Details"
              onClick={() => setShowExtraDetails(true)}
              disabled={!candidateCheck.extra_data || !candidateCheck.dummy}
            />
            <ActionsMenu.Item
              newIconLibrary
              icon={ComputerIcon}
              title="Metadata Details"
              onClick={() => setShowMetadata(true)}
              disabled={
                !candidateCheck.completed_at ||
                candidateCheck.canceled ||
                !candidateCheck.user_agent ||
                candidateCheck.dummy
              }
            />
            <ActionsMenu.Item
              newIconLibrary
              icon={ViewIcon}
              title="View Result"
              onClick={() => checkViewResult(candidateCheck, 'candidate_check')}
              disabled={
                !candidateCheck.completed_at ||
                candidateCheck.canceled ||
                !canAccessResults ||
                checkType.slug == 'candidate_questionnaire' ||
                candidateCheck.dummy ||
                !useViewResult
              }
            />

            <ActionsMenu.Item
              newIconLibrary
              icon={Download01Icon}
              title="Download Result"
              onClick={downloadResult}
              disabled={
                !candidateCheck.completed_at ||
                candidateCheck.canceled ||
                !canAccessResults ||
                !useDownloadResult ||
                candidateCheck.dummy
              }
            />

            {renewalCheck?.next_renew_at && renewalCheck.status !== 'canceled' && (
              <>
                <ActionsMenu.Item
                  newIconLibrary
                  icon={renewalCheck.approved ? StopCircleIcon : CheckmarkCircle02Icon}
                  title={renewalCheck.approved ? 'Stop Renewal' : 'Approve Renewal'}
                  onClick={() =>
                    handleRenewalUpdate(renewalCheck.id, { approved: !renewalCheck.approved })
                  }
                />
                <ActionsMenu.Item
                  newIconLibrary
                  icon={SentIcon}
                  title="Send Renewal Now"
                  onClick={() => handleRenewNow(renewalCheck.id)}
                />
                <ActionsMenu.Item
                  newIconLibrary
                  icon={CancelCircleIcon}
                  title="Cancel Renewal"
                  onClick={() => handleRenewalUpdate(renewalCheck.id, { canceled: true })}
                />
              </>
            )}

            {renewalCheck?.status === 'canceled' && (
              <ActionsMenu.Item
                newIconLibrary
                icon={RestoreBinIcon}
                title="Restore Renewal"
                onClick={() => handleRenewalUpdate(renewalCheck.id, { canceled: false })}
              />
            )}
          </ActionsMenu>
          {previousChecks?.length > 0 && (
            <div className="u-pointer" onClick={() => setPreviousChecksDropdownOpen(curr => !curr)}>
              <IconComponent
                noBackground
                type="neutral-light"
                icon={ArrowDown01Icon}
                className={cn({ [styles.iconButtonRotated]: previousChecksDropdownOpen })}
                size="small"
              />
              <span className={styles.subCheckExpandIcon}>{previousChecks.length}</span>
            </div>
          )}
        </div>
      </div>

      {previousChecks?.length > 0 && (
        <Collapse in={previousChecksDropdownOpen}>
          <div>
            {previousChecks.map(check => (
              <div
                key={check.id}
                className="card d-flex align-items-center card-with-border surface-secondary u-padding u-margin-left--large u-margin-right--large u-margin-bottom">
                <HugeiconsIcon
                  size="16"
                  icon={Clock01Icon}
                  strokeWidth="1.5"
                  className="tertiary-text u-margin-right--small"
                />
                {check.completed_at ? (
                  <Moment format={dateSlashFormat}>{check.completed_at}</Moment>
                ) : (
                  '-'
                )}
                {check.completed_at && (
                  <span className="u-margin-left--small">
                    (
                    <Moment fromNow utc>
                      {check.completed_at}
                    </Moment>
                    )
                  </span>
                )}
              </div>
            ))}
          </div>
        </Collapse>
      )}
    </div>
  );
};
